/* eslint-disable max-len */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes, { shape } from 'prop-types';
import _, { find, filter } from 'underscore';
import Promise from 'bluebird';
import { Constants as CCUIConstants } from '@bingads-webui-clientcenter/entity-utils';
import { keyVault } from '@bingads-webui-react/popup-manager';
import { TeachingBubble } from '@bingads-webui-campaign-react/popup-components';
import { LearnMoreLink, HelpIcon } from '@bingads-webui-campaign-react/help';
import { curlyTemplate } from '@bingads-webui-universal/primitive-utilities';
import { isNavigationRefreshOn } from '@bingads-webui-react/preference-utility';

import { CalloutCardWithLock, getRecommendationViewModel } from './callout-card-component/index';
import { getLogger, getConfig, getParams, odata as odataInternal } from './import-recommendation-service/index';
import { getDefaultImportRecommendationService } from './default-import-recommendation-service';
import { RecommendationStatus, settings, RecommendationID, RecommendationIDCouponIdMapping } from './constants';

const FAC_STATUS_NOT_IN_PROGRESS = CCUIConstants.RecommendationAdoptionStatus.NotInProgess;
const FAC_STATUS_ACTIVE = CCUIConstants.RecommendationAdoptionStatus.Active;

export const ImportCalloutCard = ({
  i18n, serverContext, activity, gotoPage, ccJsData, aid,
  onNext,
  shouldDisplayDelay,
  permissions,
}) => {
  //
  const [recommendation, setRecommendation] = useState(null);
  const [coupon, setCoupon] = useState(null);
  const [hasShown, setShown] = useState(false);

  const logger = useMemo(() => getLogger(activity), [activity]);
  const odata = useMemo(() => {
    const isLocal = () => {
      const hostname = document.location.hostname.toLowerCase();
      return hostname.indexOf('ads.microsoft.com') === -1 &&
          hostname.indexOf('ads-int.microsoft.com') === -1;
    };
    const odataPath = isLocal() ? 'Advertiser/V2' : 'Campaign/V2';
    return odataInternal(getConfig({
      CurrentAccount: serverContext.CurrentAccount,
      CurrentCustomer: serverContext.CurrentCustomer,
      CurrentUser: serverContext.CurrentUser,
      Culture: serverContext.CurrentLocale,
      culture: serverContext.CurrentLocale,
      CultureLcid: serverContext.CurrentUser.LocaleID,
      Omniture: serverContext.Omniture,
      'odata.token': serverContext.Token,
      'odata.path': odataPath,
      'odata.url': serverContext.ServicesEndpoints.CampaignAPI,
    }), getParams(serverContext));
  }, [serverContext]);

  // init import recommendation service
  const service = useMemo(() => {
    if (serverContext && serverContext.CurrentUser) {
      const { Id: userId, Name: userName } = serverContext.CurrentUser;
      return getDefaultImportRecommendationService({
        odata, userId, userName, logger,
      });
    }
    return null;
  }, [logger, odata, serverContext]);

  const onNextHandler = useCallback(() => {
    if (!_.isUndefined(onNext)) {
      onNext();
    }
  }, [onNext]);

  const callbacks = useMemo(() => ({
    onGotoGoogleImport: () => {
      const recId = recommendation ? recommendation.id : null;
      logger.logTrace(`[ImportCalloutCard] go to google import page, recommendation:${JSON.stringify(recommendation || '')}, RID:${recId}`);
      if (recommendation) {
        const promises = [service.clickRecommendation(recommendation)];
        if ((recommendation.id === RecommendationID.NeverUsedId || recommendation.id === RecommendationID.SmartNeverUsedId)
          && coupon && coupon.Status === FAC_STATUS_NOT_IN_PROGRESS) {
          logger.logTrace(`Update FACCoupon into active: ${JSON.stringify(coupon)}`);
          promises.push(ccJsData.FeatureAdoptionCoupon.update(
            coupon.id,
            {
              adoption: {
                ...coupon,
                Status: FAC_STATUS_ACTIVE,
              },
            }
          ).catch((err) => {
            logger.logTrace(`Failed to update FACCoupon with error ${JSON.stringify(err)}`);
          }));
        }
        Promise.all(promises)
          .then(() => {
            setRecommendation(null);
            onNextHandler();
            gotoPage('import-google', recommendation.id);
          }).catch(() => {});
      }
    },
    onGotoPinterestImport: () => {
      const recId = recommendation ? recommendation.id : null;
      logger.logTrace(`[ImportCalloutCard] go to pinterest import page, recommendation:${JSON.stringify(recommendation || '')}, RID:${recId}`);
      if (recommendation) {
        const promises = [service.clickRecommendation(recommendation)];
        if (recommendation.id === RecommendationID.PinterestNeverUsedId
          && coupon && coupon.Status === FAC_STATUS_NOT_IN_PROGRESS) {
          logger.logTrace(`Update FACCoupon into active: ${JSON.stringify(coupon)}`);
          promises.push(ccJsData.FeatureAdoptionCoupon.update(
            coupon.id,
            {
              adoption: {
                ...coupon,
                Status: FAC_STATUS_ACTIVE,
              },
            }
          ).catch((err) => {
            logger.logTrace(`Failed to update FACCoupon with error ${JSON.stringify(err)}`);
          }));
        }
        Promise.all(promises)
          .then(() => {
            setRecommendation(null);
            onNextHandler();
            gotoPage('import-pinterest', recommendation.id);
          }).catch(() => {});
      }
    },
    onGotoFacebookImport: () => {
      const recId = recommendation ? recommendation.id : null;
      logger.logTrace(`[ImportCalloutCard] go to facebook import page, recommendation:${JSON.stringify(recommendation || '')}, RID:${recId}`);
      if (recommendation) {
        const promises = [service.clickRecommendation(recommendation)];
        if (recommendation.id === RecommendationID.FacebookAudienceSupportId
          && coupon && coupon.Status === FAC_STATUS_NOT_IN_PROGRESS) {
          logger.logTrace(`Update FACCoupon into active: ${JSON.stringify(coupon)}`);
          promises.push(ccJsData.FeatureAdoptionCoupon.update(
            coupon.id,
            {
              adoption: {
                ...coupon,
                Status: FAC_STATUS_ACTIVE,
              },
            }
          ).catch((err) => {
            logger.logTrace(`Failed to update FACCoupon with error ${JSON.stringify(err)}`);
          }));
        }
        Promise.all(promises)
          .then(() => {
            setRecommendation(null);
            onNextHandler();
            gotoPage('import-facebook', recommendation.id);
          }).catch(() => {});
      }
    },
    onGoToScheduleAndHistory: () => {
      const recId = recommendation ? recommendation.id : null;
      logger.logTrace(`[ImportCalloutCard] go to history page, recommendation:${JSON.stringify(recommendation || '')}, RID:${recId}`);
      if (recommendation) {
        service.clickRecommendation(recommendation);
        setRecommendation(null);
        onNextHandler();
        gotoPage('import-schedule', recommendation.id);
      }
    },
    onDismiss: () => {
      const recId = recommendation ? recommendation.id : null;
      logger.logTrace(`[ImportCalloutCard] dismiss callout card, recommendation:${JSON.stringify(recommendation || '')}, RID:${recId}`);
      if (recommendation) {
        service.dismissRecommendation(recommendation);
        setRecommendation(null);
      }
      onNextHandler();
    },
  }), [ccJsData.FeatureAdoptionCoupon, coupon, gotoPage, logger, recommendation, service, onNextHandler]);

  const viewModel = useMemo(
    () => getRecommendationViewModel({ recommendation, i18n, callbacks }),
    [callbacks, i18n, recommendation]
  );

  const getRecommendation = useCallback(() => {
    logger.logTrace('[ImportCalloutCard] start to get highest priority recommendation');
    const getCouponPromise = ccJsData.FeatureAdoptionCoupon.findAll({ aid })// if call fails, eat the error to avoid blocking workflow
      .then(coupons =>
        filter(
          coupons,
          cpn =>
            (cpn.Status === FAC_STATUS_NOT_IN_PROGRESS || cpn.Status === FAC_STATUS_ACTIVE)
        )).catch(() => null);
    Promise.all([service.getHighestPriorityRecommendation(), getCouponPromise]).then(([value, coupons]) => {
      if (value) {
        setRecommendation(value);
        const cpn = find(
          coupons,
          item => value.id && item.FeatureId === RecommendationIDCouponIdMapping[value.id]
        );
        if (cpn) {
          logger.logTrace(`Get FACCoupon: ${JSON.stringify(cpn)}`);
          setCoupon(cpn);
        }
        setShown(true);
      } else {
        onNextHandler();
      }
    });
  }, [logger, service, ccJsData.FeatureAdoptionCoupon, aid, onNextHandler]);

  // get recommendation
  useEffect(() => {
    if (service && !recommendation && !hasShown) {
      if (!shouldDisplayDelay) {
        getRecommendation();
      } else {
        setTimeout(() => {
          getRecommendation();
        }, settings.DisplayDelayInMilliSecs);
      }
    }
  }, [hasShown, recommendation, service, getRecommendation, shouldDisplayDelay]);

  // patch Viewed status
  useEffect(() => {
    if (service && recommendation && recommendation.status === RecommendationStatus.Active) {
      logger.logTrace(`[ImportCalloutCard] start to set viewed status, recommendation:${JSON.stringify(recommendation)}, RID:${recommendation.id}`);
      service.viewRecommendation(recommendation)
        .then((value) => {
          setRecommendation(value);
          if (!value) {
            onNextHandler();
          }
        });
    }
  }, [callbacks, logger, onNextHandler, recommendation, service]);

  const [target, setTarget] = useState(null);

  useEffect(() => {
    // try to get target again once servercontext or scope changed
    setTarget(null);
    const getTarget = (retry) => {
      const isNavRefresh = isNavigationRefreshOn(permissions);
      const importMenu = document.querySelector(isNavRefresh ? '.side-nav-item-container button#import' : '.global-menu-toolbar #import');
      if (importMenu && importMenu.offsetParent) {
        const style = window.getComputedStyle(importMenu);
        if (style.display !== 'none') {
          setTarget(importMenu);
          return;
        }
      }
      if (retry < 3) {
        setTimeout(() => getTarget(retry + 1), settings.DisplayDelayInMilliSecs);
      } else {
        onNextHandler();
      }
    };
    if (!shouldDisplayDelay) {
      getTarget(0);
    } else {
      setTimeout(() => {
        getTarget(0);
      }, settings.DisplayDelayInMilliSecs);
    }
  }, [callbacks, shouldDisplayDelay, onNextHandler, serverContext, permissions]);

  if (!target || !recommendation || !viewModel || !hasShown) {
    return <></>;
  }

  const {
    title, content, contentHelpId, buttons, couponHelpId,
  } = viewModel;

  if (_.result(serverContext, ['Permissions', 'IsCentralizedPopupManagementEnabled'])) {
    const contentNode = (
      <div className="import-callout-card-content">
        {
          coupon ?
            <div className="import-callout-card-coupon">
              <span>
                {curlyTemplate(i18n.getString(_TL_('Receive {{couponValue}} Credit')))({ couponValue: i18n.formatCurrency(coupon.CouponValue, serverContext.CurrentAccount.CurrencyCode) })}
                {
                couponHelpId ?
                  <HelpIcon
                    topic={couponHelpId}
                  /> : <></>
                }
              </span>
            </div> : <></>
        }
        <span>{content}</span>
        {contentHelpId
          ? <LearnMoreLink topic={contentHelpId} linkText={i18n.getString(_TL_('Learn more'))} />
          : null}
      </div>
    );

    return (<TeachingBubble
      featureName={`import-callout-card-${_.result(recommendation, 'id')}`}
      title={title}
      content={contentNode}
      onDone={buttons[0].onClick}
      onDismiss={buttons[1].onClick}
      onClose={buttons[1].onClick}
      primaryButtonText={buttons[0].label}
      secondaryButtonText={buttons[1].label}
      targetElement={target}
      lockKey={keyVault.importRecommendation}
    />);
  }

  return (
    <CalloutCardWithLock
      i18n={i18n}
      title={title}
      content={content}
      contentHelpId={contentHelpId}
      buttons={buttons}
      target={target}
      coupon={coupon}
      couponHelpId={couponHelpId}
      currency={serverContext.CurrentAccount.CurrencyCode}
      logger={logger}
    />
  );
};

ImportCalloutCard.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  serverContext: PropTypes.shape({
    CurrentAccount: shape({
      CurrencyCode: PropTypes.string,
    }),
    CurrentUser: PropTypes.shape({
      Id: PropTypes.number,
    }),
  }).isRequired,
  activity: PropTypes.shape({}).isRequired,
  gotoPage: PropTypes.func.isRequired,
  ccJsData: PropTypes.shape({
    FeatureAdoptionCoupon: PropTypes.shape({
      findAll: PropTypes.func,
      update: PropTypes.func,
    }),
  }).isRequired,
  aid: PropTypes.number.isRequired,
  onNext: PropTypes.func,
  shouldDisplayDelay: PropTypes.bool,
  permissions: PropTypes.shape({ }).isRequired,
};

ImportCalloutCard.defaultProps = {
  onNext: _.noop,
  shouldDisplayDelay: true,
};
